"use client";

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { CreditCard, Loader2, Phone, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

import { useStore } from '@/store';
import {
  AuthError,
  type OtpChallenge,
  type OtpMode,
  requestOtp,
  resendOtp,
  toStoreUser,
  verifyOtp,
} from '@/api/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

type Props = {
  mode: OtpMode;
  title: string;
  subtitle: string;
  submitLabel: string;
  footer: React.ReactNode;
};

const RESEND_SECONDS = 30;

/**
 * Sign-in and sign-up are the same two steps against the same API - a phone
 * number, then the code it was sent - so both pages render this and differ only
 * in wording and which endpoint prefix they use.
 */
export default function PhoneAuthForm({ mode, title, subtitle, submitLabel, footer }: Props) {
  const { dispatch } = useStore();
  const router = useRouter();

  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [challenge, setChallenge] = useState<OtpChallenge | null>(null);
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) {
      return;
    }
    const timer = setTimeout(() => setCooldown(cooldown - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const describe = (error: unknown) =>
    error instanceof AuthError ? error.message : 'Something went wrong. Please try again.';

  const sendCode = async (event: React.FormEvent) => {
    event.preventDefault();
    const digits = phone.replace(/\D+/g, '');
    if (!/^[6-9]\d{9}$/.test(digits)) {
      toast.error('Enter a valid 10-digit mobile number.');
      return;
    }

    setBusy(true);
    try {
      const result = await requestOtp(digits, mode);
      setChallenge(result);
      setCooldown(RESEND_SECONDS);
      toast.success(`Code sent to ${result.phone_masked}`);
    } catch (error) {
      toast.error(describe(error));
    } finally {
      setBusy(false);
    }
  };

  const confirmCode = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!challenge) {
      return;
    }
    if (!/^\d{6}$/.test(otp)) {
      toast.error('Enter the six-digit verification code.');
      return;
    }

    setBusy(true);
    try {
      const { account, session } = await verifyOtp(challenge.challenge_id, otp, mode);
      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: { user: toStoreUser(account), token: session.token },
      });
      toast.success('Signed in.');
      router.push('/');
    } catch (error) {
      toast.error(describe(error));
    } finally {
      setBusy(false);
    }
  };

  const sendAgain = async () => {
    if (!challenge || cooldown > 0) {
      return;
    }
    setBusy(true);
    try {
      const result = await resendOtp(challenge.challenge_id, mode);
      setChallenge(result);
      setCooldown(RESEND_SECONDS);
      toast.success('A new code is on its way.');
    } catch (error) {
      toast.error(describe(error));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center py-12 px-4">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <div className="mb-4 inline-flex h-14 w-14 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-accent shadow-gold">
            <CreditCard className="h-7 w-7 text-primary-foreground" />
          </div>
          <h1 className="font-display text-2xl font-bold">{title}</h1>
          <p className="mt-2 text-muted-foreground">{subtitle}</p>
        </div>

        {challenge === null ? (
          <form onSubmit={sendCode} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="phone">Mobile Number</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="phone"
                  type="tel"
                  inputMode="numeric"
                  autoComplete="tel"
                  maxLength={10}
                  placeholder="9876543210"
                  className="pl-10"
                  value={phone}
                  onChange={(event) => setPhone(event.target.value.replace(/\D+/g, ''))}
                  required
                />
              </div>
              <p className="text-xs text-muted-foreground">
                We will send a six-digit code to this number.
              </p>
            </div>

            <Button type="submit" variant="gold" size="lg" className="w-full" disabled={busy}>
              {busy ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Sending code...
                </>
              ) : (
                submitLabel
              )}
            </Button>
          </form>
        ) : (
          <form onSubmit={confirmCode} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="otp">Verification Code</Label>
              <div className="relative">
                <ShieldCheck className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="otp"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  placeholder="123456"
                  className="pl-10 tracking-[0.4em]"
                  value={otp}
                  onChange={(event) => setOtp(event.target.value.replace(/\D+/g, ''))}
                  required
                  autoFocus
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Sent to {challenge.phone_masked}.{' '}
                <button
                  type="button"
                  onClick={() => {
                    setChallenge(null);
                    setOtp('');
                  }}
                  className="font-medium text-primary hover:underline"
                >
                  Change number
                </button>
              </p>
            </div>

            <Button type="submit" variant="gold" size="lg" className="w-full" disabled={busy}>
              {busy ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Verifying...
                </>
              ) : (
                'Verify and continue'
              )}
            </Button>

            <button
              type="button"
              onClick={sendAgain}
              disabled={busy || cooldown > 0}
              className="w-full text-sm text-muted-foreground hover:text-foreground disabled:cursor-not-allowed disabled:opacity-60"
            >
              {cooldown > 0 ? `Resend code in ${cooldown}s` : 'Resend code'}
            </button>
          </form>
        )}

        <p className="mt-6 text-center text-sm text-muted-foreground">{footer}</p>
      </div>
    </div>
  );
}

export function AuthFooterLink({ href, prompt, action }: { href: string; prompt: string; action: string }) {
  return (
    <>
      {prompt}{' '}
      <Link href={href} className="font-medium text-primary hover:underline">
        {action}
      </Link>
    </>
  );
}
